import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE } from './config';
import AdminSidebar from './AdminSidebar';
import '../css/AdminManagement.css';
import '../css/AdminResultLog.css';

const RESULT_LABELS = {
  team1_win: 'T1 WIN',
  draw: 'DRAW',
  team2_win: 'T2 WIN'
};

const resultLabel = value => RESULT_LABELS[String(value || '').toLowerCase()] || 'ยังไม่ระบุ';
const formatDateTime = value => value
  ? new Date(value).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
  : '-';

function AdminResultLog() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const groupId = Number(searchParams.get('groupId') || localStorage.getItem('last_group_id') || 0);
  const actor = useMemo(() => JSON.parse(localStorage.getItem('user') || 'null'), []);
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLogs = useCallback(async (targetPage = page, query = search) => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE}/api/admin/match-result-logs?groupId=${groupId}&page=${targetPage}&limit=20&q=${encodeURIComponent(query.trim())}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'โหลด Result Log ไม่สำเร็จ');
      setLogs(data.logs || []);
      setTotal(Number(data.total || 0));
      setTotalPages(Number(data.totalPages || 1));
    } catch (loadError) {
      setLogs([]);
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [groupId, page, search]);

  useEffect(() => {
    if (!actor || String(actor.role || '').toLowerCase() !== 'admin') {
      navigate('/', { replace: true });
      return undefined;
    }
    const timer = window.setTimeout(() => loadLogs(page, search), 250);
    return () => window.clearTimeout(timer);
  }, [actor, loadLogs, navigate, page, search]);

  if (!actor || String(actor.role || '').toLowerCase() !== 'admin') return null;

  return (
    <main className="admin-management admin-shell-with-sidebar admin-result-log-page">
      <AdminSidebar active="result-logs" groupId={groupId} />
      <header className="admin-management-header">
        <div>
          <button type="button" className="admin-back" onClick={() => navigate('/dashboard')}>← Dashboard</button>
          <h1>📝 Result Log</h1>
          <p>ประวัติการบันทึกและแก้ไขผล เฉพาะ Group #{groupId || '-'}</p>
        </div>
        <button type="button" onClick={() => loadLogs(page, search)} disabled={loading}>🔄 รีเฟรช</button>
      </header>

      <section className="admin-result-log-search">
        <input
          type="search"
          value={search}
          onChange={event => { setSearch(event.target.value); setPage(1); }}
          placeholder="ค้นหาเลข Match, ชื่อผู้บันทึก หรือชื่อ Group..."
        />
        <span>ทั้งหมด {total} รายการ</span>
      </section>

      {error && <div className="admin-message error">{error}</div>}
      {loading ? <div className="admin-empty">กำลังโหลด Result Log...</div> : logs.length === 0 ? (
        <div className="admin-empty">ยังไม่มีประวัติการบันทึกหรือแก้ไขผล</div>
      ) : (
        <section className="admin-result-log-list">
          {logs.map(log => (
            <article key={log.id}>
              <div className="admin-result-log-title">
                <strong>Match #{log.group_match_no || log.game_match_id}</strong>
                <span className={log.action_type === 'SUBMITTED' ? 'is-submitted' : 'is-edited'}>
                  {log.action_type === 'SUBMITTED'
                    ? 'ผู้บันทึกผล'
                    : `ผู้แก้ไขครั้งที่ ${Math.max(1, Number(log.audit_sequence || 1) - 1)}`}
                </span>
              </div>
              <p>{log.group_name || `Group #${log.group_id}`} • Court {log.court_no || '-'}</p>
              <div className="admin-result-log-change">
                <span>{resultLabel(log.previous_result)}</span><b>→</b><strong>{resultLabel(log.new_result)}</strong>
              </div>
              <footer>
                <span>👤 {log.changed_by_name} (ID {log.changed_by_user_id})</span>
                <span>{String(log.changed_by_role).toLowerCase() === 'admin' ? 'Admin' : 'ผู้เล่น'}</span>
                <time>{formatDateTime(log.changed_at)}</time>
              </footer>
            </article>
          ))}
        </section>
      )}

      {totalPages > 1 && (
        <nav className="admin-result-log-pagination" aria-label="หน้า Result Log">
          <button type="button" disabled={page <= 1 || loading} onClick={() => setPage(value => Math.max(1, value - 1))}>← ก่อนหน้า</button>
          <span>หน้า {page} / {totalPages}</span>
          <button type="button" disabled={page >= totalPages || loading} onClick={() => setPage(value => Math.min(totalPages, value + 1))}>ถัดไป →</button>
        </nav>
      )}
    </main>
  );
}

export default AdminResultLog;
